import React, { useEffect, useState, useRef } from 'react';
import { ClockIcon, ChevronDownIcon } from 'lucide-react';
interface TimePickerDropdownProps {
  value: string; // 24-hour format HH:MM
  onChange: (value: string) => void;
  darkMode?: boolean;
  error?: boolean;
  required?: boolean;
  label?: string;
}
/**
 * Generate all 5-minute interval time options for the full day (00:00 - 23:55)
 */
const generateTimeOptions = (): string[] => {
  const options: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 5) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      options.push(timeString);
    }
  }
  return options;
};
/**
 * Convert 24-hour time (HH:MM) to 12-hour format with AM/PM
 */
const formatTo12Hour = (time24: string): string => {
  if (!time24 || !time24.includes(':')) return '';
  const [hourStr, minuteStr] = time24.split(':');
  const hour = parseInt(hourStr);
  const minute = minuteStr;
  if (isNaN(hour) || hour < 0 || hour > 23) return '';
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute} ${period}`;
};
/**
 * Get current time in HH:MM format
 */
const getCurrentTime = (): string => {
  const now = new Date();
  const hours = now.getHours().toString().padStart(2, '0');
  const minutes = Math.floor(now.getMinutes() / 5) * 5; // Round to nearest 5-minute interval
  const minutesStr = minutes.toString().padStart(2, '0');
  return `${hours}:${minutesStr}`;
};
export const TimePickerDropdown: React.FC<TimePickerDropdownProps> = ({
  value,
  onChange,
  darkMode = false,
  error = false,
  required = false,
  label
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectedItemRef = useRef<HTMLLIElement>(null);
  // Generate all time options (00:00 - 23:55 in 5-minute intervals)
  const timeOptions = generateTimeOptions();
  // Preselect current time if no value provided
  useEffect(() => {
    if (!value) {
      const currentTime = getCurrentTime();
      onChange(currentTime);
    }
  }, [value, onChange]);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);
  // Scroll to selected item when dropdown opens
  useEffect(() => {
    if (isOpen && selectedItemRef.current) {
      setTimeout(() => {
        selectedItemRef.current?.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }, 100);
    }
  }, [isOpen]);
  // Filter options based on search term
  const filteredOptions = timeOptions.filter(time => {
    const display12Hour = formatTo12Hour(time);
    const searchLower = searchTerm.toLowerCase();
    return time.includes(searchLower) || display12Hour.toLowerCase().includes(searchLower);
  });
  // Handle time selection
  const handleSelectTime = (time: string) => {
    onChange(time);
    setIsOpen(false);
    setSearchTerm('');
  };
  // Get display value (12-hour format)
  const displayValue = value ? formatTo12Hour(value) : '';
  return <div className="relative" ref={dropdownRef} data-id="element-11537">
      {label && <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-11538">
          {label}
          {required && <span className="text-red-500 ml-1" data-id="element-11539">*</span>}
        </label>}
      {/* Trigger button */}
      <div className={`flex items-center justify-between w-full rounded-lg border ${error ? 'border-red-500 focus-within:border-red-500' : darkMode ? 'bg-navy-900/50 border-navy-700/50 focus-within:border-[#10B981] focus-within:ring-1 focus-within:ring-[#10B981]/20' : 'bg-white border-gray-300 focus-within:border-[#10B981] focus-within:ring-1 focus-within:ring-[#10B981]/20'} px-4 py-2.5 cursor-pointer shadow-sm transition-all`} onClick={() => setIsOpen(!isOpen)} data-id="element-11540">
        <div className="flex items-center flex-grow" data-id="element-11541">
          <ClockIcon className={`h-4 w-4 ${darkMode ? 'text-[#3B82F6]' : 'text-[#3B82F6]'} mr-2.5 flex-shrink-0`} data-id="element-11542" />
          {!isOpen ? <span className={`text-sm ${displayValue ? darkMode ? 'text-white' : 'text-gray-900' : darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-11543">
              {displayValue || 'Select time'}
            </span> : <input ref={searchInputRef} type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} onClick={e => e.stopPropagation()} placeholder="Search time..." className={`w-full border-none outline-none ${darkMode ? 'bg-transparent text-white placeholder-gray-500' : 'bg-transparent text-gray-900 placeholder-gray-400'} focus:ring-0`} data-id="element-11544" />}
        </div>
        <ChevronDownIcon className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'} ml-2 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} data-id="element-11545" />
      </div>
      {/* Dropdown menu */}
      {isOpen && <div className={`absolute z-[9999] w-full mt-2 rounded-lg shadow-xl ${darkMode ? 'bg-navy-800 border border-navy-700/50' : 'bg-white border border-gray-200'} max-h-64 overflow-auto`} style={darkMode ? {
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)'
    } : {
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05)'
    }} data-id="element-11546">
          <ul className="py-1" data-id="element-11547">
            {filteredOptions.length > 0 ? filteredOptions.map(time => {
          const display12Hour = formatTo12Hour(time);
          const isSelected = time === value;
          return <li key={time} ref={isSelected ? selectedItemRef : null} className={`px-4 py-2.5 cursor-pointer flex items-center justify-between ${isSelected ? darkMode ? 'bg-navy-700/50' : 'bg-gray-100' : darkMode ? 'hover:bg-navy-700/30' : 'hover:bg-gray-50'} ${darkMode ? 'text-white' : 'text-gray-900'} transition-colors`} onClick={() => handleSelectTime(time)} data-id="element-11548">
                    <span className="text-sm" data-id="element-11549">{display12Hour}</span>
                    {isSelected && <span className="text-xs text-[#10B981]" data-id="element-11550">✓</span>}
                  </li>;
        }) : <li className={`px-4 py-2.5 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-11551">
                No times found
              </li>}
          </ul>
        </div>}
      {/* Scrollbar styling */}
      <style jsx data-id="element-11552">{`
        ul::-webkit-scrollbar {
          width: 6px;
        }
        ul::-webkit-scrollbar-track {
          background: transparent;
        }
        ul::-webkit-scrollbar-thumb {
          background-color: ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
          border-radius: 3px;
        }
        ul::-webkit-scrollbar-thumb:hover {
          background-color: ${darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
        }
      `}</style>
    </div>;
};